<template>
    <div id="CImageInput">
        <input
            ref="input"
            :accept="accept"
            class="hidden"
            :disabled="disabled"
            :multiple="multipleFiles"
            type="file"
            @change="handleFile($event)"
        />

        <div v-if="gallery">
            <CButton :disabled="disabled" variant="primary" @click="openFileBrowser()">Nahrať obrázky</CButton>

            <div v-if="images.length" class="mt-4 grid grid-cols-6 gap-2">
                <div v-for="(image, index) in images" :key="index" class="relative">
                    <div
                        class="absolute flex size-full cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-500/75 text-3xl opacity-0 transition-all hover:opacity-100"
                        @click.stop="openPreview(image)"
                    >
                        <div
                            :id="index.toString()"
                            class="flex size-12 items-center justify-center rounded-full bg-white dark:bg-surface-card transition-all hover:cursor-pointer hover:text-red-400"
                            @click.stop="handleClick"
                        >
                            <CIcon class="pointer-events-none" name="close" />
                        </div>
                    </div>

                    <img
                        class="object-fit aspect-square cursor-pointer rounded-md object-cover object-center"
                        :src="image"
                    />
                </div>
            </div>
        </div>

        <div
            v-else
            class="rounded-2xl border border-gray-200 bg-gray-50 p-6 shadow-lg dark:border-default dark:bg-surface-card"
        >
            <div class="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
                <!-- Circular image preview -->
                <div class="relative">
                    <div
                        class="size-36 shrink-0 overflow-hidden rounded-full border-2 bg-white dark:bg-subtle"
                        :class="
                            displayedImage
                                ? 'border-gray-200 dark:border-default'
                                : 'border-dashed border-gray-300 dark:border-default'
                        "
                    >
                        <img
                            v-if="displayedImage && typeof displayedImage === 'string' && displayedImage.length > 0"
                            class="size-full object-cover"
                            loading="lazy"
                            :src="displayedImage"
                        />
                        <div v-else class="flex size-full items-center justify-center text-gray-400 dark:text-txt-body">
                            <CIcon name="user" class="size-12" />
                        </div>
                    </div>

                    <!-- Delete button (positioned outside circle) -->
                    <button
                        v-if="modelValue"
                        type="button"
                        class="absolute -right-1 -top-1 flex size-7 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-md transition-colors hover:bg-red-600 dark:border-default"
                        :disabled="disabled"
                        title="Odstrániť"
                        @click="clear()"
                    >
                        <svg class="size-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Upload section (only when no image) -->
                <div v-if="!displayedImage" class="flex flex-1 flex-col items-center gap-3 sm:items-start">
                    <CButton :disabled="disabled" variant="primary" @click="openFileBrowser()"> Nahrať fotku </CButton>
                    <p v-if="description" class="text-sm dark:text-txt-body">JPG, PNG, WebP • Min. 300×300 px</p>
                </div>

                <!-- Change photo link (when image exists) -->
                <div v-else class="flex flex-1 flex-col items-center gap-2 sm:items-start">
                    <button type="button" class="link text-sm" :disabled="disabled" @click="openFileBrowser()">
                        Zmeniť fotku
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { readFileAsDataUrl } from '#shared/utils'
import ImageCropperModal from '@/components/modals/ImageCropperModal.vue'
import { computed, onMounted, ref } from 'vue'
import { useModal } from 'vue-final-modal'

// Browser-compatible MIME type to extension mapping
const getExtensionFromMimeType = (mimeType: string): string | null => {
    const mimeToExt: Record<string, string> = {
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/svg+xml': 'svg',
        'image/bmp': 'bmp',
        'image/tiff': 'tiff',
        'application/pdf': 'pdf',
        'video/mp4': 'mp4',
        'video/webm': 'webm',
        'video/ogg': 'ogv',
        'audio/mpeg': 'mp3',
        'audio/ogg': 'ogg',
        'audio/wav': 'wav',
        'text/plain': 'txt',
        'text/html': 'html',
        'text/css': 'css',
        'text/javascript': 'js',
        'application/json': 'json',
        'application/zip': 'zip',
        'application/x-rar-compressed': 'rar'
    }
    return mimeToExt[mimeType] || null
}

type CropperModalProps = {
    image: string
    size?: number
    stencilProps?: Record<string, unknown>
}

const openImageCropperModal = (modalProps: CropperModalProps) => {
    return new Promise((resolve) => {
        const { open, close } = useModal({
            component: ImageCropperModal,
            attrs: {
                image: modalProps.image,
                ...(typeof modalProps.size === 'number' ? { size: modalProps.size } : {}),
                ...(modalProps.stencilProps ? { stencilProps: modalProps.stencilProps } : {}),
                onClose(result?: any) {
                    close()
                    resolve(result)
                }
            }
        })
        open()
    })
}

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    imagePlaceholder: {
        type: String,
        default: ''
    },
    crop: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    multipleFiles: {
        type: Boolean,
        default: false
    },
    gallery: {
        type: Boolean,
        default: false
    },
    accept: {
        type: String as () => HTMLInputElement['accept'],
        default: undefined
    },
    acceptExtensions: {
        type: Array as () => string[],
        default: () => []
    },
    orgImageGallery: {
        type: Array as () => string[],
        default: () => []
    },
    size: {
        type: Number,
        default: undefined
    },
    stencilProps: {
        type: Object,
        default: undefined
    },
    description: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:model-value'])

const input = ref<HTMLInputElement | null>(null)
const images = ref<string[]>([])

const inputElement = computed((): HTMLInputElement => {
    return input.value as HTMLInputElement
})

const displayedImage = computed((): string => {
    return props.modelValue || props.imagePlaceholder || ''
})

onMounted(() => {
    images.value = props.orgImageGallery
})

const openPreview = (image: string) => {
    if (process.client) {
        const img = new Image()
        img.src = image

        const page = window.open(undefined, '_blank')
        page?.document.write(img.outerHTML)
    }
}

const openFileBrowser = () => {
    inputElement.value.click()
}

const clear = () => {
    emit('update:model-value', '')
}

const loadImage = async (arr: any[], files: File[], i: number) => {
    const file = files[i]
    if (!file) return

    const img = new Image()

    const result = (await readFileAsDataUrl(file)) as string

    img.src = result

    if (img.complete) {
        arr.push(img)
    } else {
        await loadImage(arr, files, i)
    }
}

const handleFile = async (event: Event) => {
    const target = event.target as HTMLInputElement
    const fileList = target.files

    if (!fileList?.length) return

    const files = Array.from(fileList)?.filter((file) => isValidType(file))

    if (!files.length) return

    if (props.multipleFiles) {
        const arr: any[] = []

        for (let i = 0; i < files.length; i++) {
            await loadImage(arr, files, i)
        }

        arr.forEach((item: HTMLImageElement) => {
            if (item.width > item.height) {
                images.value.push(compressImage(item, 800 / item.width))
            } else {
                images.value.push(compressImage(item, 600 / item.height))
            }
        })

        inputElement.value.value = ''

        emit('update:model-value', images.value)
    } else {
        const file = files[0]
        if (!file) return

        const image = await readFileAsDataUrl(file)

        inputElement.value.value = ''

        if (props.crop) {
            const croppedImage = await openImageCropperModal({
                image: image as string,
                size: props.size,
                stencilProps: props.stencilProps
            })

            if (croppedImage) {
                emit('update:model-value', croppedImage)
            }

            return
        }

        emit('update:model-value', image)
    }
}

const isValidType = (file: File) => {
    if (!props.accept && !props.acceptExtensions.length) return true

    if (props.acceptExtensions.length) {
        const fileExtension = getExtensionFromMimeType(file.type) || ''
        const isValidExtension = props.acceptExtensions.includes(fileExtension)
        if (isValidExtension) return true
    }

    if (props.accept) {
        const acceptTypes = props.accept.replaceAll(',', '|')
        const isValidAccept = file.type.match(acceptTypes)
        if (isValidAccept) return true
    }

    return false
}

const compressImage = (imgToCompress: any, compressRatio: number) => {
    if (process.client) {
        // showing the compressed image
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        canvas.width = imgToCompress.width * compressRatio
        canvas.height = imgToCompress.height * compressRatio

        context!.fillStyle = 'white'
        context!.fillRect(0, 0, canvas.width, canvas.height)
        context!.drawImage(imgToCompress, 0, 0, canvas.width, canvas.height)

        return canvas.toDataURL('image/jpeg')
    }
    return ''
}

const handleClick = (event: any) => {
    const index = Number(event.target.id)
    images.value.splice(index, 1)
}
</script>
